<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells position="end">
        <app-button v-if="is_general_manager" @click.native="onSyncButtonClick" theme="warn" size="small" ref="sync_button">
          Sync hotel database
        </app-button>
      </app-cells>
      <app-cells>
        <app-input
          @input.native="onHotelsSearch"
          v-model="filter_search"
          class="input--200"
          type="search"
          placeholder="Search by name"
          name="search"
        />
        <app-select
          @input="fetchItems"
          v-model="filter_chain"
          :options="chain_options"
          :reduce="item => item.id"
          placeholder="Chain"
          class="select--200"
        />
        <app-select
          @input="onTerritoryChanged"
          v-model="filter_territory"
          :options="territories_options"
          :reduce="item => item.id"
          placeholder="Territory"
          class="select--200"
        />
        <app-select
          @input="onRegionChanged"
          v-model="filter_region"
          :options="regions_options"
          :reduce="item => item.id"
          placeholder="Region"
          class="select--200"
          :readonly="!filter_territory"
        />
        <app-select
          @input="fetchItems"
          v-model="filter_area"
          :options="areas_options"
          :reduce="item => item.id"
          placeholder="Area"
          class="select--200"
          :readonly="!filter_region"
        />
        <app-filters
          v-model="filter_active"
          :filters="active_filters"
          name="filterActive"
          @change="fetchItems"
        />
      </app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{'table-default--loading': table_preloader}"
      >
        <template #h__name><div class="table-default__left">Name</div></template>
        <template #h__region><div class="table-default__left">Region</div></template>
        <template #h__area><div class="table-default__left">Area</div></template>
        <template #name="{ row }">
          <div class="table-default__left">
            <router-link :to="{ name: 'hotels-database-general', params: { id: row.id } }" class="table-default__link-border">
              {{ row.name }}
            </router-link>
          </div>
        </template>
        <template #star="{ row }">
          <star-rating
            :rating="row.rating"
            :read-only="true"
            :star-size="22"
            :show-rating="false"
            :inline="true"
            :rounded-corners="true"
            :increment="0.5"
            active-color="#eb9514"
            inactive-color="#d1d1d1"
          ></star-rating>
        </template>
        <template #external="{ row }">
          <app-cells position="center" :indent="false">
            <div class="table-default__rating">
              <template v-if="row.booking">
                <a :href="row.booking" target="_blank">
                  <img src="@/assets/img/booking-active-icon.svg" alt=""> {{ row.booking_rating }}
                </a>
              </template>
              <img v-else src="@/assets/img/booking-not-active-icon.svg" alt="">
            </div>
            <div class="table-default__rating">
              <template v-if="row.tripadvisor">
                <a :href="row.tripadvisor" target="_blank">
                  <img src="@/assets/img/tripadvisor-active-icon.svg" alt=""> {{ row.tripadvisor_rating }}
                </a>
              </template>
              <img v-else src="@/assets/img/tripadvisor-not-active-icon.svg" alt="">
            </div>
          </app-cells>
        </template>
        <template #region="{ row }"><div class="table-default__left">{{ row.region.name }}</div></template>
        <template #area="{ row }"><div class="table-default__left">{{ row.area.name }}</div></template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
    </template>
  </div>
</template>

<script>
import {
  getHotelsChain,
  getHotelsList,
  getLocationAreas,
  getLocationRegions,
  getLocationTerritories,
  postHotelsImport
} from '@/http'
import debounce from 'lodash/debounce'
import StarRating from 'vue-star-rating'

export default {
  name: 'HotelsDatabaseList',
  components: { StarRating },
  data() {
    return {
      is_loaded: false,
      table_preloader: true,
      table: {
        items: [],
        columns: ['name', 'star', 'type.name', 'external', 'region', 'area'],
        options: {
          headings: {
            'name': 'Name',
            'star': 'Star',
            'type.name': 'Type',
            'external': 'External',
            'region': 'Region',
            'area': 'Area',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      filter_search: '',
      filter_chain: null,
      filter_territory: null,
      filter_region: null,
      filter_area: null,
      filter_active: 'true',
      territories_options: [],
      regions_options: [],
      areas_options: [],
      chain_options: [],
      active_filters: [
        { value: 'true', label: 'Active' },
        { value: '', label: 'Not active' },
      ],
    }
  },
  computed: {
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
  },
  created() {
    this.fetchItems()
    getLocationTerritories().then(res => { this.territories_options = res.data })
    getHotelsChain().then(res => { this.chain_options = res.data })
  },
  methods: {
    onSyncButtonClick() {
      this.$refs.sync_button.preload = true
      postHotelsImport()
        .finally(() => { this.$refs.sync_button.preload = false })
        .then(res => {
          this.$notify({
            type: res.data.started ? 'success' : 'info',
            title: res.data.detail,
          })
        })
    },
    fetchItems() {
      getHotelsList({
        page: this.pagination.page,
        page_size: 20,
        search: this.filter_search,
        chain: this.filter_search ? null : this.filter_chain,
        territory: this.filter_search ? null : this.filter_territory,
        region: this.filter_search ? null : this.filter_region,
        area: this.filter_search ? null : this.filter_area,
        active: this.filter_search ? null : !!this.filter_active,
      })
        .then(res => {
          this.table.items = res.data.results
          this.pagination.pages = res.data.pages
          this.pagination.count = res.data.count
          this.is_loaded = true
          this.table_preloader = false
        })
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onHotelsSearch() {
      this.filter_territory = null
      this.filter_region = null
      this.filter_area = null
      this.searchHotels(this)
    },
    searchHotels: debounce(vm => {
      vm.fetchItems()
    }, 350),
    onTerritoryChanged() {
      this.filter_region = null
      this.filter_area = null
      this.fetchItems()
      getLocationRegions({ territory: this.filter_territory })
        .then(res => { this.regions_options = res.data })
    },
    onRegionChanged() {
      this.filter_area = null
      this.fetchItems()
      getLocationAreas({ region: this.filter_region })
        .then(res => { this.areas_options = res.data })
    }
  }
}
</script>
<style lang="sass">
.vue-star-rating
  transform: translateY(2px)
</style>
